import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { dealersData, mapCenter } from '../data/dealers';

const DealerMap = ({ onDealerSelect, selectedDealer }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [markerClusterer, setMarkerClusterer] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const initMap = async () => {
      const loader = new Loader({
        apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyD62hfSrhYQ5c3PYoa2JlcuahMqQQx3lpQ', // Przykładowy klucz - zastąp rzeczywistym
        version: 'weekly',
        libraries: ['places']
      });

      try {
        const google = await loader.load();
        
        const mapInstance = new google.maps.Map(mapRef.current, {
          center: mapCenter,
          zoom: window.innerWidth < 768 ? 5 : 6,
          minZoom: 5,
          streetViewControl: false,
          zoomControlOptions: {
            position: google.maps.ControlPosition.LEFT_BOTTOM
          }
        });

        setMap(mapInstance);

        // Tworzenie InfoWindow
        const infoWindowInstance = new google.maps.InfoWindow();
        setInfoWindow(infoWindowInstance);

        // Dodawanie znaczników
        const markersArray = [];
        dealersData.forEach((dealer) => {
          const position = new google.maps.LatLng(
            parseFloat(dealer.Latitude),
            parseFloat(dealer.Longitude)
          );

          const marker = new google.maps.Marker({
            position: position,
            title: `${dealer.Name}\n${dealer.ZipCode} ${dealer.City}, ${dealer.Street}`,
            map: mapInstance,
            id: dealer.GlobalID,
            icon: {
              url: process.env.PUBLIC_URL + '/img/map/map-marker-black.png',
              anchor: new google.maps.Point(16, 32)
            }
          });

          // Dodanie event listener dla kliknięcia w znacznik
          marker.addListener('click', () => {
            infoWindowInstance.setContent(
              `<div style="padding: 10px;">
                <h3 style="margin: 0 0 5px 0; font-size: 14px;">${dealer.Name}</h3>
                <p style="margin: 0; font-size: 12px;">${dealer.ZipCode} ${dealer.City}, ${dealer.Street}</p>
                <p style="margin: 5px 0 0 0; font-size: 11px; color: #666;">
                  Godziny otwarcia: ${dealer.openFrom} - ${dealer.openTo}
                </p>
              </div>`
            );
            infoWindowInstance.open(mapInstance, marker);

            // Wybór dealera
            onDealerSelect(dealer);
          });

          markersArray.push(marker);
        });

        setMarkers(markersArray);

        // Dodanie clusteringu zgodnie z projektem źródłowym
        // MarkerClusterer jest już załadowany globalnie w HTML
        if (window.MarkerClusterer) {
          const clusterPath = process.env.PUBLIC_URL + '/img/map/m3/m';
          const textColor = '#fff';
          
          const clusterStyles = [
            {url: clusterPath + '1.png', width: 53, height: 52, textColor: textColor},
            {url: clusterPath + '2.png', width: 56, height: 55, textColor: textColor},
            {url: clusterPath + '3.png', width: 66, height: 65, textColor: textColor},
            {url: clusterPath + '4.png', width: 78, height: 77, textColor: textColor},
            {url: clusterPath + '5.png', width: 90, height: 89, textColor: textColor}
          ];

          const markerCluster = new window.MarkerClusterer(mapInstance, markersArray, {
            styles: clusterStyles
          });

          setMarkerClusterer(markerCluster);
        } else {
          console.warn('MarkerClusterer library not available');
        }

      } catch (error) {
        console.error('Błąd podczas ładowania mapy:', error);
      }
    };

    if (!map && showMap) {
      initMap();
    }
  }, [map, onDealerSelect, showMap]);

  // Filtrowanie dealerów na podstawie wyszukiwania
  useEffect(() => {
    if (!map || !markers.length || !markerClusterer) return;

    const filteredDealers = dealersData.filter(dealer => {
      if (!searchTerm) return true;
      
      const search = searchTerm.toLowerCase();
      const replacement = {
        'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 
        'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z'
      };
      
      let normalizedSearch = search;
      Object.keys(replacement).forEach(key => {
        const pattern = new RegExp(key, 'g');
        normalizedSearch = normalizedSearch.replace(pattern, replacement[key]);
      });
      normalizedSearch = normalizedSearch.replace(/[^0-9a-z]/g, '');

      return dealer.search.includes(normalizedSearch);
    });

    // Ukrywanie/pokazywanie znaczników
    markers.forEach(marker => {
      const dealer = dealersData.find(d => d.GlobalID === marker.id);
      const isVisible = filteredDealers.some(d => d.GlobalID === dealer.GlobalID);
      marker.setVisible(isVisible);
    });

    // Aktualizacja clusteringu
    if (markerClusterer) {
      markerClusterer.clearMarkers();
      const visibleMarkers = markers.filter(marker => marker.getVisible());
      markerClusterer.addMarkers(visibleMarkers);
    }

    // Centrowanie mapy na widocznych znacznikach
    if (filteredDealers.length > 0 && filteredDealers.length < dealersData.length) {
      const bounds = new window.google.maps.LatLngBounds();
      filteredDealers.forEach(dealer => {
        bounds.extend(new window.google.maps.LatLng(
          parseFloat(dealer.Latitude),
          parseFloat(dealer.Longitude)
        ));
      });
      map.fitBounds(bounds);
    } else if (searchTerm === '') {
      map.setCenter(mapCenter);
      // map.setZoom(window.innerWidth < 768 ? 5 : 6);
    }

  }, [searchTerm, map, markers, markerClusterer]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (map) {
      map.setCenter(mapCenter);
      map.setZoom(window.innerWidth < 768 ? 5 : 6);
    }
  };

  const handleShowMap = () => {
    setShowMap(true);
  };

  return (
    <div className="dealer-map-container">
      {!showMap ? (
        <button 
          onClick={handleShowMap}
          className="dealer-map-trigger"
        >
          Znajdź na mapie
        </button>
      ) : (
        <>
          <div className="dealer-search-container">
            <input
              type="text"
              placeholder="Wpisz adres lub nazwę dealera"
              value={searchTerm}
              onChange={handleSearchChange}
              className="dealer-search-input"
            />
            {searchTerm && (
              <button onClick={clearSearch} className="dealer-search-clear">
                Wyczyść
              </button>
            )}
          </div>
          
          <div 
            ref={mapRef} 
            className="dealer-map"
            style={{ 
              width: '100%', 
              height: '400px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </>
      )}
      
      {selectedDealer && (
        <div className="selected-dealer-info">
          <h4>Wybrany dealer:</h4>
          <p><strong>{selectedDealer.Name}</strong></p>
          <p>{selectedDealer.ZipCode} {selectedDealer.City}</p>
          <p>{selectedDealer.Street}</p>
          <p>Godziny otwarcia: {selectedDealer.openFrom} - {selectedDealer.openTo}</p>
        </div>
      )}
    </div>
  );
};

export default DealerMap; 